import type {
  MenuItemOptionProps,
  MenuOptionGroupProps,
  TextProps,
} from '@chakra-ui/react';
import {
  Button,
  Menu,
  MenuButton,
  MenuItemOption as ChakraMenuItemOption,
  MenuList,
  MenuOptionGroup as ChakraMenuOptionGroup,
  Text,
} from '@chakra-ui/react';
import { CaretDown, Check } from '@time-line/components/icons';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import type { CartLineUpdateInput } from 'services/shopify';
import type { CartLineItem as CartLineItemType } from 'types/cart/CartLineItem';

import { hasServings, isSubscriptionProduct } from '../utils/typeInference';

interface CartLineItemDropdownProps {
  lineItem: CartLineItemType;
  /* We pass the ID in the CartLineItem parent component */
  onUpdateItem: (input: Omit<CartLineUpdateInput, 'id'>) => void;
}

const MenuOptionGroup = ({ ...props }: TextProps & MenuOptionGroupProps) => (
  <Text
    as={ChakraMenuOptionGroup}
    mt="4xs"
    mx="2xs"
    mb="5xs"
    size="detail"
    color="blackAlpha.700"
    fontWeight="medium"
    {...props}
  />
);

const MenuItemOption = ({ ...props }: TextProps & MenuItemOptionProps) => (
  <Text
    as={ChakraMenuItemOption}
    pr="2xs"
    py="4xs"
    size="overline"
    _disabled={{
      opacity: 1,
    }}
    icon={<Check boxSize="16px" mt="-3px" />}
    {...props}
  />
);

export function CartLineItemDropdown({
  lineItem,
  onUpdateItem,
}: CartLineItemDropdownProps) {
  const { data: shopifyProduct } = useShopifyProduct(
    lineItem.merchandise.product.handle,
  );

  if (!shopifyProduct) return null;

  const otpOption = {
    label: 'One-time purchase',
    value: shopifyProduct.variants[0].id,
  };

  const currentSelection = lineItem.sellingPlanAllocation?.sellingPlan.id ?? '';
  const currentLabel = hasServings(lineItem.merchandise)
    ? lineItem.merchandise.title
    : (lineItem.sellingPlanAllocation?.sellingPlan.title ?? '');

  return (
    <Menu autoSelect={false}>
      <MenuButton
        mt={{ base: '3xs', md: '2xs' }}
        as={Button}
        variant="unstyled"
        rightIcon={<CaretDown mt="-4px" ml="-6px" />}
        /* HACK: Seems like this component applies a lot of overriding styles,
         * hence access via sx. Also see box-shadow below. */
        sx={{
          svg: {
            width: '16px',
            height: '16px',
          },
        }}
        display="flex"
        alignItems="center"
        textDecorationLine="underline"
        textUnderlineOffset="4px"
      >
        {currentLabel}
      </MenuButton>
      <MenuList
        zIndex={10}
        // INFO: The !important below is to override the removal of box-shadows from .js-focus-visible and [data-js-focus-visible]
        boxShadow="0 0 0 1px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px rgba(0, 0, 0, 0.06), 0px 6px 24px 0px rgba(0, 0, 0, 0.06) !important"
        background="gray.50"
        borderRadius="lg"
        textAlign="left"
        width="264px"
        height="auto"
        py="4xs"
        defaultValue={currentSelection}
      >
        <MenuOptionGroup title="Pay full price" type="radio">
          <MenuItemOption
            value={otpOption.value}
            isChecked={otpOption.value === currentSelection}
            isDisabled={otpOption.value === currentSelection}
            onClick={() => {
              onUpdateItem({
                merchandiseId: otpOption.value,
                sellingPlanId: null,
                attributes: [],
              });
            }}
          >
            {otpOption.label}
          </MenuItemOption>
        </MenuOptionGroup>
        <MenuOptionGroup title="Subscribe & Save" type="radio">
          {isSubscriptionProduct(shopifyProduct) &&
            shopifyProduct.subscriptions.map((subscription) => (
              <MenuItemOption
                key={subscription.sellingPlan.id}
                value={subscription.sellingPlan.id}
                isChecked={subscription.sellingPlan.id === currentSelection}
                isDisabled={subscription.sellingPlan.id === currentSelection}
                onClick={() => {
                  onUpdateItem({
                    merchandiseId: subscription.id,
                    sellingPlanId: subscription.sellingPlan.id,
                    attributes: [],
                  });
                }}
              >
                {hasServings(lineItem.merchandise)
                  ? subscription.title
                  : subscription.sellingPlan.title}
              </MenuItemOption>
            ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}
